import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaProjectDiagram } from '@react-icons/all-files/fa/FaProjectDiagram';
import { FaLayerGroup } from '@react-icons/all-files/fa/FaLayerGroup';
import { FaPlus } from '@react-icons/all-files/fa/FaPlus';
import { FaHome } from '@react-icons/all-files/fa/FaHome';
import { FaCheckCircle } from '@react-icons/all-files/fa/FaCheckCircle';
import { FaTimesCircle } from '@react-icons/all-files/fa/FaTimesCircle';
import Breadcrumb from '../../commons/Breadcrumb';
import PageHeader from '../../commons/PageHeader';
import ProfaseDataGrid from '../../commons/Datagrid';
import Confirm, { useConfirm } from '../../commons/Confirm';
import PageAlert, { usePageAlert } from '../../commons/PageAlert';
import { ConfigButton, DeleteButton, DesignButton, EditButton, RenderActions } from '../../commons/DataGridActions';
import { useAppDispatch, useAppSelector } from '../../store';
import { deleteEntity, getEntities } from '../../manager/workflow-def.reducer';
import { formatWorkflowPeriodicidadLabel } from '../../shared/workflow-periodicidad';

const boolCell = (params: any) =>
  params.value
    ? <FaCheckCircle style={{ color: '#16a34a', fontSize: 15 }} />
    : <FaTimesCircle style={{ color: '#dc2626', fontSize: 15 }} />;

const objCell = (params: any) =>
  params.value?.nombre ?? params.value?.data?.label ?? params.value?.idNodo ?? params.value?.id ?? '—';

export default function WorkflowListPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const entities      = useAppSelector(s => s.workflowDef.entities);
  const totalItems    = useAppSelector(s => s.workflowDef.totalItems);
  const loading       = useAppSelector(s => s.workflowDef.loading);
  const updating      = useAppSelector(s => s.workflowDef.updating);
  const updateSuccess = useAppSelector(s => s.workflowDef.updateSuccess);

  const [rows, setRows]             = useState<any[]>([]);
  const [lastParams, setLastParams] = useState<any>(null);
  const pendingDeleteRef            = useRef(false);

  const { alert, showToast } = usePageAlert();
  const { confirmConfig, ask, dismiss, setConfirmLoading } = useConfirm();

  useEffect(() => { setRows(entities ?? []); }, [entities]);

  useEffect(() => {
    const msg = (location.state as { successMsg?: string } | null)?.successMsg;
    if (msg) {
      showToast('success', msg);
      window.history.replaceState({}, '');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (updateSuccess) {
      const params = lastParams ?? { page: 0, sort: 'id,asc', filters: [], size: 15 };
      dispatch(getEntities(params));
      if (pendingDeleteRef.current) {
        pendingDeleteRef.current = false;
        dismiss();
        showToast('success', 'Workflow eliminado correctamente.');
      }
    }
  }, [updateSuccess, dispatch, lastParams, dismiss, showToast]);

  useEffect(() => {
    setConfirmLoading(updating);
  }, [updating, setConfirmLoading]);

  const fetchData = (params: any) => {
    setLastParams(params);
    dispatch(getEntities(params));
  };

  const renderActions = (params: any) => (
    <RenderActions>
      <ConfigButton onClick={() => navigate(`/workflow/${params.row.id}/config`)} />
      <DesignButton onClick={() => navigate(`/workflow/${params.row.id}/diseno`)} />
      <EditButton onClick={() => navigate(`/workflow/${params.row.id}`)} />
      <DeleteButton onClick={() => {
        ask(
          <>¿Confirmas que deseas eliminar <strong>{params.row.nombre}</strong>? Esta acción no se puede deshacer.</>,
          () => {
            pendingDeleteRef.current = true;
            dispatch(deleteEntity(params.row.id));
          },
          { title: 'Eliminar Workflow', confirmLabel: 'Eliminar', variant: 'danger' },
        );
      }} />
    </RenderActions>
  );

  const cols = [
    { field: 'id',            headerName: 'ID',             flex: 0.4 },
    { field: 'nombre',        headerName: 'Nombre',         flex: 1.5 },
    { field: 'periodicidad',  headerName: 'Periodicidad',   flex: 1,
      renderCell: (p: any) => formatWorkflowPeriodicidadLabel(p.value) },
    { field: 'periodo',       headerName: 'Período',        flex: 0.8 },
    { field: 'porDefecto',    headerName: 'Por Defecto',    flex: 0.7, renderCell: boolCell },
    { field: 'executorClass', headerName: 'Executor Class', flex: 1.5 },
    { field: 'workflowClase', headerName: 'Clase',          flex: 0.8, renderCell: objCell },
    { field: 'nodoInicio',    headerName: 'Nodo Inicio',    flex: 1,   renderCell: objCell },
    { field: 'plantilla',     headerName: 'Plantilla',      flex: 0.8,
      renderCell: (p: any) => p.value?.nombre ?? <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>Sin plantilla</span> },
    {
      field: '__actions',
      headerName: '',
      flex: 1.3,
      filterable: false,
      renderCell: renderActions,
    },
  ];

  const filters = [
    { label: 'Nombre',         name: 'nombre' },
    { label: 'Periodicidad',   name: 'periodicidad' },
    { label: 'Executor Class', name: 'executorClass' },
  ];

  return (
    <div style={s.page}>
      <Breadcrumb items={[
        { label: 'Inicio', to: '/', icon: <FaHome /> },
        { label: 'Workflow', icon: <FaProjectDiagram /> },
      ]} />

      <div style={s.toolbar}>
        <PageHeader
          icon={<FaProjectDiagram />}
          iconColor="#10b981"
          title="Workflow"
          subtitle="Listado de workflows"
        />
        <div style={s.actions}>
          <button
            type="button"
            className="btn btn-sm btn-light d-inline-flex align-items-center justify-content-center"
            style={s.iconBtn}
            title="Nuevo workflow"
            aria-label="Nuevo workflow"
            onClick={() => navigate('/workflow/new')}
          >
            <FaPlus />
          </button>
          <button
            type="button"
            style={s.clasesBtn}
            onClick={() => navigate('/workflow/clases')}
          >
            <FaLayerGroup style={{ marginRight: 6 }} />
            Clases
          </button>
        </div>
      </div>

      <Confirm config={confirmConfig} />
      {alert && <PageAlert alert={alert} />}

      <ProfaseDataGrid
        cols={cols}
        title=""
        filters={filters}
        fetch={fetchData}
        rowCount={totalItems}
        loading={loading}
        rows={rows}
      />
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  page: { display: 'flex', flexDirection: 'column', gap: 24 },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 12,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  iconBtn: {
    width: 36,
    height: 32,
    padding: 0,
  },
  clasesBtn: {
    display: 'flex',
    alignItems: 'center',
    padding: '9px 18px',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    background: '#6366f1',
  },
};
